<template>
  <div>
    <div
      v-if="
        shippingMethod &&
          (shippingMethod.status == 'aborted' || shippingMethod.status == 'badaddress')
      "
      class="text-danger mb-2"
    >
      <font-awesome-icon icon="triangle-exclamation" size="sm" />
      <strong :data-error="shippingMethod.status" class="ml-1">{{
        methodValidationMessage
      }}</strong>
    </div>

    <PlatformRenderer>
      <template v-slot:onMobile>
        <WCFormGroup>
          <label for="order-details__order-type" class="font-weight-bold">{{
            $t('orderType')
          }}</label>
          <WCSelect
            class="bg-light"
            id="order-details__order-type"
            v-model="form.orderType.id"
            @input="setShippingMethod"
            :state="
              shippingMethod &&
              (shippingMethod.status == 'aborted' || shippingMethod.status == 'badaddress')
                ? 'invalid'
                : null
            "
          >
            <option
              v-for="option in sortedShippingMethods"
              :key="option.id"
              :value="option.id"
              :data-option="option.id"
            >
              <template v-if="option.cost === 0">{{ option.name }}</template>
              <template v-else
                >{{ option.name }} ({{ option.cost | currency }} {{ $t('fee') }})</template
              ></option
            >
          </WCSelect>
        </WCFormGroup>
      </template>
      <template v-slot:onDesktop>
        <WCFormGroup class="w-25">
          <label for="order-details__order-type" class="font-weight-bold">{{
            $t('orderType')
          }}</label>
          <WCSelect
            class="bg-light"
            id="order-details__order-type"
            v-model="form.orderType.id"
            @input="setShippingMethod"
            :state="
              shippingMethod &&
              (shippingMethod.status == 'aborted' || shippingMethod.status == 'badaddress')
                ? 'invalid'
                : null
            "
          >
            <option
              v-for="option in sortedShippingMethods"
              :key="option.id"
              :value="option.id"
              :data-option="option.id"
            >
              <template v-if="option.cost === 0">{{ option.name }}</template>
              <template v-else
                >{{ option.name }} ({{ option.cost | currency }} {{ $t('fee') }})</template
              ></option
            >
          </WCSelect>
        </WCFormGroup>
      </template>
    </PlatformRenderer>

    <WCTimeRestrictedItemList
      :reservationDates="reservationDates"
      :lineItems="lineItems"
      :shippingMethod="shippingMethod"
      :reservationRequired="reservationRequired"
      @removeitem="$emit('removeitem', $event)"
      @removeallitems="$emit('removeallitems', $event)"
    />

    <PlatformRenderer>
      <template v-slot:onMobile>
        <WCMobileReservation
          v-if="reservationDates && reservationDates.length > 0"
          :options="reservationDates"
          @change="$emit('reservation', $event)"
          v-model="form.reservation"
        />
      </template>
      <template v-slot:onDesktop>
        <WCReservation
          v-if="reservationDates && reservationDates.length > 0"
          :options="reservationDates"
          @change="$emit('reservation', $event)"
          v-model="form.reservation"
        />
      </template>
    </PlatformRenderer>

    <div
      v-if="reservationRequired && (!reservationDates || reservationDates.length == 0)"
      class="text-center mt-2 font-wieght-bold"
    >
      {{ $t('noReservationTimes') }}
    </div>

    <template v-if="form.orderType.addressRequired">
      <label class="mt-2 h5 font-weight-bold">{{ $t('deliveryAddress') }}</label>
      <WCShippingAddress
        :shipping="shipping"
        @form-changed="formChanged"
        @update:valid="
          $emit('update:valid', $event);
          canContinue = $event;
        "
      />
    </template>
    <div class="d-flex justify-content-between my-3 mt-lg-5">
      <button
        id="identity-form_goback-button"
        class="btn btn-primary btn-block w-25 stage-button"
        @click="$emit('goback')"
      >
        {{ $t('goBack') }}
      </button>
      <button
        id="identity-form_continue-button"
        class="btn btn-primary btn-block w-25 m-0 stage-button"
        :disabled="!canContinue"
        @click="$emit('continue')"
      >
        {{ $t('continue') }}
      </button>
    </div>
  </div>
</template>

<script>
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import WCFormGroup from '@/modules/forms/components/WCFormGroup/WCFormGroup.vue';
import WCSelect from '@/modules/forms/components/WCSelect/WCSelect.vue';
import WCReservation from '@/modules/checkout/components/WCReservation/WCReservation.vue';
import WCMobileReservation from '@/modules/checkout/components/WCMobileReservation/WCMobileReservation.vue';
import WCShippingAddress from '@/modules/checkout/components/WCShippingAddress/WCShippingAddress.vue';
import WCTimeRestrictedItemList from '@/modules/checkout/components/WCTimeRestrictedItemList/WCTimeRestrictedItemList.vue';
import { dynamicSort } from '@/utils';

export default {
  components: {
    WCFormGroup,
    WCSelect,
    WCReservation,
    WCShippingAddress,
    WCTimeRestrictedItemList,
    PlatformRenderer,
    WCMobileReservation,
  },
  props: {
    shippingMethod: {
      type: Object,
    },
    shippingMethods: {
      type: Array,
    },
    reservationDate: {
      type: String,
    },
    reservationTime: {
      type: String,
    },
    shipping: {
      type: Object,
    },
    lineItems: {
      type: Array,
    },
    activeStage: {
      type: Number,
    },
  },
  watch: {
    activeStage: {
      immediate: true,
      handler() {
        if (this.activeStage === 2) {
          this.canContinue = true;
          this.$emit('update:valid', true);
          // If shipping method invalid, then disable continue button
          if (
            this.shippingMethod &&
            (this.shippingMethod.status === 'aborted' ||
              this.shippingMethod.status === 'badaddress' ||
              this.shippingMethod.status === 'noaddress')
          ) {
            this.canContinue = false;
            this.$emit('update:valid', false);
          }
        }
      },
    },
    shippingMethod: {
      immediate: true,
      handler() {
        if (this.activeStage === 2) {
          this.canContinue = true;
          this.$emit('update:valid', true);
          // If shipping method invalid, then disable continue button
          if (
            this.shippingMethod &&
            (this.shippingMethod.status === 'aborted' ||
              this.shippingMethod.status === 'badaddress' ||
              this.shippingMethod.status === 'noaddress')
          ) {
            this.canContinue = false;
            this.$emit('update:valid', false);
          }
        }
      },
    },
  },
  created() {
    if (this.reservationDates && this.reservationDates.length > 0) {
      this.form.reservation = {
        date: this.reservationDate ? this.reservationDate : this.reservationDates[0].date,
        time: this.reservationTime ? this.reservationTime : this.reservationDates[0].times[0].start,
      };

      this.$emit('reservation', {
        date: this.form.reservation.date,
        time: this.form.reservation.time,
      });
    }

    if (!this.shippingMethod) {
      this.$emit('method', this.form.orderType);
    }
  },
  data() {
    return {
      canContinue: true,
      form: {
        orderType: {
          id: this.shippingMethod ? this.shippingMethod.id : this.shippingMethods[0].id,
          name: this.shippingMethod ? this.shippingMethod.name : this.shippingMethods[0].name,
          cost: this.shippingMethod ? this.shippingMethod.cost : this.shippingMethods[0].cost,
          addressRequired: this.shippingMethod
            ? this.shippingMethod.addressRequired
            : this.shippingMethods[0].addressRequired,
          reservationRequired: this.shippingMethod
            ? this.shippingMethod.reservationRequired
            : this.shippingMethods[0].reservationRequired,
          status: this.shippingMethod ? this.shippingMethod.status : this.shippingMethods[0].status,
        },
        reservation: {
          date: '',
          time: '',
        },
      },
    };
  },
  computed: {
    reservationDates() {
      return this.shippingMethods.find(sm => sm.id === this.form.orderType.id).reservationDates;
    },
    methodValidationMessage() {
      let message = 'No shipping method validation error found';
      if (this.shippingMethod.status === 'aborted') {
        message = this.shippingMethod.reason ? this.shippingMethod.reason : 'Order not eligible';
      }
      if (this.shippingMethod.status === 'badaddress') {
        message = 'The selected order type is not available for this address.';
      }

      return message;
    },
    reservationRequired() {
      return this.shippingMethod ? this.shippingMethod.reservationRequired : false;
    },
    /**
     * Sort shipping methods in ascending order
     */
    sortedShippingMethods() {
      return dynamicSort(this.shippingMethods, 'name');
    },
  },
  methods: {
    setShippingMethod() {
      const newMethod = this.shippingMethods.find(sm => sm.id === this.form.orderType.id);
      this.form.orderType = { ...newMethod };
      const newDates = newMethod.reservationDates;
      if (newDates && newDates.length > 0) {
        this.form.reservation.date = newDates[0].date;
        this.form.reservation.time = newDates[0].times[0].start;
      }

      this.$emit('method', this.form.orderType);
      this.$emit('reservation', {
        date: this.form.reservation.date,
        time: this.form.reservation.time,
      });

      this.canContinue = true;
      this.$emit('update:valid', true);
      // If new method invalid, then disable continue button
      if (newMethod && (newMethod.status === 'aborted' || newMethod.status === 'badaddress')) {
        this.canContinue = false;
        this.$emit('update:valid', false);
      }
    },
    formChanged($event) {
      // If user tries inputing new address, then enable continue button
      if (this.shippingMethod && this.shippingMethod.status === 'badaddress') {
        this.canContinue = true;
        this.$emit('update:valid', true);
      }

      this.$emit('form-changed', $event);
    },
  },
};
</script>

<style lang="scss" scoped>
.stage-button {
  min-width: 33%;
}
</style>
